import React, { useEffect, useState } from "react";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { AudioProvider } from "../Contexts/AudiosContext";
import AppRoutes from "./AppRoutes";
import Loading from "../Components/Loading";
import { LocaleProvider } from "../Contexts/LocaleContext";
import AudioPlayer from "../Components/AudioPlayer";
import VideoPiPOverlay from "../Components/VideoPiPOverlay";
import { CookiesProvider } from "react-cookie";
import 'react-toastify/dist/ReactToastify.css';
import { VideoProvider } from "../Contexts/VideosContext";
import InternetPage from "../Pages/NoInternetConnection";
import PageSeo from "./PageSeo";
import LoginServices from "../Services/Login";
import UTMService from "../Services/UTMService";
import { buildUTMPayload, hasUTMParams, stripUTMFromSearch } from "../utils/utmUtils";
import {
  applyAccountNoticeFromResult,
  clearAccountNotice,
  getAccountNoticeType,
} from "../utils/accountNotice";
import MobileDevPageInfoBar from "../Pages/ResponsivePages/Common/MobileDevPageInfoBar";
import DesktopDevPageInfoBar from "../Pages/ResponsivePages/Common/DesktopDevPageInfoBar";
import MetaPixel from "../Components/MetaPixel";
import FavoriteFeedbackPopup from "../Components/FavoriteFeedbackPopup";

// Browser-safe normalize helper used in auth checks
const normalize = (v?: string | null): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v);
  return (!s || s.trim() === "" || s === "null" || s === "undefined") ? null : s;
};

/////////////////////
// UTMHandler component
/////////////////////
function UTMHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Skip UTM tracking during react-snap prerender
    if ((window as any).__IS_SNAP_RENDER) return;

    if (!hasUTMParams(location.search)) return;

    const cleanSearch = stripUTMFromSearch(location.search);
    const cleanPath = location.pathname + cleanSearch;

    if (sessionStorage.getItem("utm_tracked")) {
      if (location.pathname + location.search !== cleanPath) {
        navigate(cleanPath, { replace: true });
      }
      return;
    }

    const payload = buildUTMPayload(location.search);
    sessionStorage.setItem("utm_tracked", "true");

    UTMService.track(payload).catch(() => {
      // Tracking must never break UX
    });

    navigate(cleanPath, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

/////////////////////
// Account notice gate — any other route returns to /account-notice
/////////////////////
function AccountNoticeGuard() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const logdetails: string[] = [
      "action=AccountNoticeGuard",
      `path=${location.pathname}`,
    ];
    const noticeType = getAccountNoticeType();
    if (!noticeType) {
      logdetails.push("notice=none");
      console.log("[App]", logdetails.join(" | "));
      return;
    }

    if (location.pathname === "/account-notice") {
      logdetails.push(`notice=${noticeType}`, "status=on_notice_page");
      console.log("[App]", logdetails.join(" | "));
      return;
    }

    logdetails.push(`notice=${noticeType}`, "status=redirect_to_notice");
    console.log("[App]", logdetails.join(" | "));
    navigate("/account-notice", { replace: true });
  }, [location.pathname, navigate]);

  return null;
}

/////////////////////
// Main App
/////////////////////
function App(props: any) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const logdetails: string[] = ["action=checkAuthStatus"];
      const userId = normalize(localStorage.getItem("UserId") ?? "");
      const token = normalize(localStorage.getItem("Token") ?? "");

      console.log("=== App Start: Auth Check ===");
      if (userId && token && userId !== null && userId !== undefined && userId !== "") {
        try {
          logdetails.push(`userId=${userId}`, "step=refreshTokenV2");
          console.log(`-> Refreshing token for user: ${userId}`);
          // Passing current token as fcmToken as found in legacy code pattern
          const response: any = await LoginServices.refreshTokenV2(userId, token);
          console.log("<- Refresh Response:", response);

          if (response && (response.status === true || response.status === 200) && response.result) {
            console.log("-> Token refreshed successfully.");
            localStorage.setItem("Token", response.result.token);
            // Ensure we update other keys if returned, or keep existing
            if (response.result.userId) localStorage.setItem("UserId", response.result.userId);
            if (response.result.signKey) localStorage.setItem("SignKey", response.result.signKey);

            // Update Image from response directly (no need for extra profile call)
            if (response.result.imagePath) {
              console.log("-> Updating 'Image' from refresh response.");
              localStorage.setItem("Image", response.result.imagePath);
            }

            const noticeType = applyAccountNoticeFromResult(response.result);
            logdetails.push(`notice=${noticeType || "none"}`, "status=ok");
          } else {
            console.warn("-> Refresh failed or invalid response. Clearing auth.");
            clearAuthData();
            logdetails.push("status=refresh_failed");
          }
        } catch (e) {
          console.error("-> Error during token refresh:", e);
          clearAuthData();
          logdetails.push("status=error");
        }
      } else {
        console.log("-> No user/token found in local storage. Proceeding as guest.");
        clearAccountNotice();
        logdetails.push("status=guest", "notice=cleared");
      }
      console.log("[App]", logdetails.join(" | "));
      setAuthChecked(true);
      console.log("=== App Start: Auth Check Complete ===");
    };

    const clearAuthData = () => {
      console.log("-> Clearing localStorage auth data.");
      clearAccountNotice();
      const itemsToRemove = [
        "UserId", "Token", "SignKey", "UserName",
        "Image", "Phone", "Email", "fixedUserId", "signInMode"
      ];
      itemsToRemove.forEach(item => localStorage.removeItem(item));
    };

    checkAuthStatus();
  }, []);

  // Hold render until auth check finishes so the notice gate sees the latest flags
  if (!authChecked) {
    return <Loading />;
  }

  return (
    isOnline ? (
      <LocaleProvider>
        <CookiesProvider>
          <React.Suspense fallback={<Loading />}>
            <BrowserRouter>
              <UTMHandler />
              <AccountNoticeGuard />
              {/* Meta Pixel initialization and pageview tracking */}
              <MetaPixel />
              <PageSeo />
              <FavoriteFeedbackPopup />
              <VideoProvider>
                <AudioProvider>
                  <AppRoutes />
                  {/* Dev-only: current route bar (mobile / desktop) */}
                  <MobileDevPageInfoBar />
                  <DesktopDevPageInfoBar />
                  <AudioPlayer />
                  <VideoPiPOverlay />
                </AudioProvider>
              </VideoProvider>
            </BrowserRouter>
          </React.Suspense>
        </CookiesProvider>
      </LocaleProvider>
    ) : (
      <InternetPage />
    )
  );
}

export default App;
